/**
 * useScheduleData.ts
 * Owns the Machine Schedule data layer so the page component stays thin
 * (Machine Schedule UX overhaul — M1 "seamless core").
 *
 * Responsibilities:
 *   - Fetch the active machine list once on mount.
 *   - Fetch bookings + conflicts whenever the visible date window changes.
 *   - Expose a *quiet* refresh that does NOT toggle the blocking `loading`
 *     flag (so optimistic drag edits and background polling never unmount the
 *     grid / flash a spinner).
 *   - `applyLocalBookingChange` mutates a booking in place (optimistic update)
 *     and returns the prior placement so the caller can build an undo entry.
 *
 * This replaces the bespoke fetch/refetch logic that lived in
 * MachineSchedulePage and full-refetched (unmounting the grid) after each drag.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  fetchMachineBookings,
  fetchScheduleConflicts,
  fetchMachineCapabilities,
} from '../services/scheduleBookings';
import { fetchMachines } from '../services/scheduleMachines';
import type { MachineBookingCalendar, LabSection, ConflictSummary } from '../types/schedule';

export interface MachineSummary {
  machineKey: string;
  code: string;
  name: string;
  labSection: LabSection | null;
  /** Migration 120: second-level grouping (test family) — admin-editable on the Machines page. */
  machineGroup?: string | null;
}

/** A booking placement — the mutable fields a drag/keyboard edit can change. */
export interface BookingPlacementPatch {
  StartDate?: string;
  EndDate?: string;
  MachineKey?: string;
}

/** Prior placement returned by applyLocalBookingChange (for undo). */
export interface BookingPlacementSnapshot {
  StartDate: string;
  EndDate: string;
  MachineKey: string;
}

interface UseScheduleDataResult {
  bookings: MachineBookingCalendar[];
  machines: MachineSummary[];
  conflicts: ConflictSummary | null;
  /**
   * taskTypeKey → set of machineKeys capable of running it. Drives valid
   * drop targets when a booking is dragged across rows.
   */
  capabilityByTaskType: Map<string, Set<string>>;
  /** Blocking load — true only before the first paint of bookings or machines. */
  loading: boolean;
  /** Background refresh in flight (non-blocking; grid stays mounted). */
  refreshing: boolean;
  error: string | null;
  setError: (msg: string | null) => void;
  /** Reload bookings + conflicts for the current window. quiet=true → no blocking spinner. */
  reload: (opts?: { quiet?: boolean }) => Promise<void>;
  /** Quiet conflict-only refresh (after an optimistic edit). */
  reloadConflicts: () => Promise<void>;
  /**
   * Optimistically apply a placement change to a booking already in state.
   * Recomputes machine-derived fields when MachineKey changes.
   * Returns the prior placement (for undo), or null if the key isn't found.
   */
  applyLocalBookingChange: (
    key: string,
    patch: BookingPlacementPatch,
  ) => BookingPlacementSnapshot | null;
  /** Remove a booking from local state (optimistic delete). Returns the removed row. */
  removeLocalBooking: (key: string) => MachineBookingCalendar | null;
  /** Re-insert a previously removed booking (undo of delete). */
  restoreLocalBooking: (booking: MachineBookingCalendar) => void;
}

export function useScheduleData(startDate: string, endDate: string): UseScheduleDataResult {
  const [bookings, setBookings] = useState<MachineBookingCalendar[]>([]);
  const [machines, setMachines] = useState<MachineSummary[]>([]);
  const [conflicts, setConflicts] = useState<ConflictSummary | null>(null);
  const [capabilityByTaskType, setCapabilityByTaskType] = useState<Map<string, Set<string>>>(
    () => new Map(),
  );
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [loadingMachines, setLoadingMachines] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track whether the first bookings load has completed, so subsequent loads
  // for the same/another window can render as non-blocking refreshes.
  const hasLoadedOnce = useRef(false);

  // Keep the latest window in refs so reload()/reloadConflicts() are stable.
  const startRef = useRef(startDate);
  const endRef = useRef(endDate);
  useEffect(() => { startRef.current = startDate; }, [startDate]);
  useEffect(() => { endRef.current = endDate; }, [endDate]);

  const machinesByKey = useRef(new Map<string, MachineSummary>());
  useEffect(() => {
    machinesByKey.current = new Map(machines.map((m) => [m.machineKey, m]));
  }, [machines]);

  // Mirror bookings into a ref so the optimistic mutators can read the current
  // value synchronously (the setState updater may run later than the return).
  const bookingsRef = useRef<MachineBookingCalendar[]>([]);
  useEffect(() => { bookingsRef.current = bookings; }, [bookings]);

  // ── fetch bookings + conflicts ────────────────────────────────────────────
  const reload = useCallback(async (opts?: { quiet?: boolean }) => {
    const start = startRef.current;
    const end = endRef.current;
    const quiet = opts?.quiet ?? false;
    if (quiet || hasLoadedOnce.current) setRefreshing(true);
    else setLoadingBookings(true);
    setError(null);
    try {
      const [data, conflictData] = await Promise.all([
        fetchMachineBookings({ startDate: start, endDate: end }),
        fetchScheduleConflicts({ startDate: start, endDate: end }),
      ]);
      setBookings(data);
      setConflicts(conflictData);
      hasLoadedOnce.current = true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bookings.');
    } finally {
      setLoadingBookings(false);
      setRefreshing(false);
    }
  }, []);

  const reloadConflicts = useCallback(async () => {
    try {
      const conflictData = await fetchScheduleConflicts({
        startDate: startRef.current,
        endDate: endRef.current,
      });
      setConflicts(conflictData);
    } catch {
      // Conflict refresh is best-effort; never block the UI on it.
    }
  }, []);

  // Reload whenever the window changes.
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload reads window from refs
  }, [startDate, endDate]);

  // ── fetch machine list once ────────────────────────────────────────────────
  useEffect(() => {
    setLoadingMachines(true);
    fetchMachines()
      .then((rows) =>
        setMachines(
          rows.map((m) => ({
            machineKey: m.MachineKey,
            code: m.Code,
            name: m.Name,
            labSection: m.LabSection,
            machineGroup: (m as { MachineGroup?: string | null }).MachineGroup ?? null,
          })),
        ),
      )
      .catch((err) =>
        setError(err instanceof Error ? err.message : 'Failed to load machines.'),
      )
      .finally(() => setLoadingMachines(false));
  }, []);

  // ── fetch machine capabilities once (taskTypeKey → capable machineKeys) ─────
  useEffect(() => {
    fetchMachineCapabilities()
      .then((rows) => {
        const map = new Map<string, Set<string>>();
        for (const r of rows) {
          if (!r.TaskTypeKey) continue;
          const set = map.get(r.TaskTypeKey) ?? new Set<string>();
          set.add(r.MachineKey);
          map.set(r.TaskTypeKey, set);
        }
        setCapabilityByTaskType(map);
      })
      .catch(() => {
        // Capability map is an enhancement; absence just means no drop constraint.
      });
  }, []);

  // ── optimistic mutators ─────────────────────────────────────────────────────
  const applyLocalBookingChange = useCallback(
    (key: string, patch: BookingPlacementPatch): BookingPlacementSnapshot | null => {
      const existing = bookingsRef.current.find((b) => b.MachineBookingKey === key);
      if (!existing) return null;
      const prev: BookingPlacementSnapshot = {
        StartDate: existing.StartDate,
        EndDate: existing.EndDate,
        MachineKey: existing.MachineKey,
      };
      setBookings((current) =>
        current.map((b) => {
          if (b.MachineBookingKey !== key) return b;
          const next: MachineBookingCalendar = { ...b };
          if (patch.StartDate !== undefined) next.StartDate = patch.StartDate;
          if (patch.EndDate !== undefined) next.EndDate = patch.EndDate;
          if (patch.MachineKey !== undefined && patch.MachineKey !== b.MachineKey) {
            next.MachineKey = patch.MachineKey;
            const m = machinesByKey.current.get(patch.MachineKey);
            if (m) {
              next.MachineCode = m.code;
              next.MachineName = m.name;
              next.MachineLabSection = m.labSection;
            }
          }
          return next;
        }),
      );
      return prev;
    },
    [],
  );

  const removeLocalBooking = useCallback((key: string): MachineBookingCalendar | null => {
    const removed = bookingsRef.current.find((b) => b.MachineBookingKey === key) ?? null;
    if (removed) setBookings((current) => current.filter((b) => b.MachineBookingKey !== key));
    return removed;
  }, []);

  const restoreLocalBooking = useCallback((booking: MachineBookingCalendar) => {
    setBookings((current) =>
      current.some((b) => b.MachineBookingKey === booking.MachineBookingKey)
        ? current
        : [...current, booking],
    );
  }, []);

  return {
    bookings,
    machines,
    conflicts,
    capabilityByTaskType,
    loading: (loadingBookings && !hasLoadedOnce.current) || loadingMachines,
    refreshing,
    error,
    setError,
    reload,
    reloadConflicts,
    applyLocalBookingChange,
    removeLocalBooking,
    restoreLocalBooking,
  };
}
